import { useMemo, useRef, useState, type DragEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { Button, Card, SelectField } from "@atlas/ui/web";
import { atlas } from "../../../lib/sdk";
import {
  QUERY_UNIDADES_OPERAVEIS,
  listarUnidadesOperaveis,
  rotuloUnidade,
} from "../../../lib/unidades-operaveis";
import type { AdminCargaInicialResumo, ServidorCampoConfig } from "@atlas/sdk";
import { AndamentoEnvio } from "../../../components/AndamentoEnvio";
import { CamposEditor } from "./_camposEditor";
import { DEFAULT_CAMPOS_FALLBACK } from "./_defaults";

/** Carga da base por retorno existente ou pelo pacote completo de tombamento ALECE. */
export function AdminServidoresImportar() {
  const [params] = useSearchParams();
  const qc = useQueryClient();
  const prefeituras = useQuery({ queryKey: QUERY_UNIDADES_OPERAVEIS, queryFn: listarUnidadesOperaveis });
  const [prefId, setPrefId] = useState<string>(params.get("prefeituraId") ?? "");
  const prefIdNum = Number(prefId);
  const [competencia, setCompetencia] = useState<string>("");
  const [formato, setFormato] = useState<"retorno" | "carga_inicial">(
    params.get("formato") === "carga_inicial" ? "carga_inicial" : "retorno",
  );
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [resumo, setResumo] = useState<AdminCargaInicialResumo | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const configQ = useQuery({
    queryKey: ["admin", "servidor-campos-config", prefIdNum],
    queryFn: () => atlas.admin.getServidorCamposConfig(prefIdNum),
    enabled: !!prefId,
  });

  const [rascunho, setRascunho] = useState<ServidorCampoConfig[] | null>(null);
  const campos = rascunho ?? configQ.data?.config?.campos ?? DEFAULT_CAMPOS_FALLBACK;
  const dirty = useMemo(
    () => rascunho != null && JSON.stringify(rascunho) !== JSON.stringify(configQ.data?.config?.campos ?? []),
    [rascunho, configQ.data?.config?.campos],
  );

  const saveConfig = useMutation({
    mutationFn: (lista: ServidorCampoConfig[]) => atlas.admin.updateServidorCamposConfig(prefIdNum, lista),
    onSuccess: () => {
      setRascunho(null);
      qc.invalidateQueries({ queryKey: ["admin", "servidor-campos-config", prefIdNum] });
    },
  });

  const enviar = useMutation({
    mutationFn: async (file: File) =>
      atlas.admin.cargaInicial(prefIdNum, await file.arrayBuffer(), competencia.trim() || undefined, formato),
    onSuccess: (r) => {
      setResumo(r);
      if (!r.rejeitado) {
        setArquivo(null);
        qc.invalidateQueries({ queryKey: ["admin", "servidores"] });
      }
    },
  });

  function aceitar(file: File | undefined) {
    if (!file) return;
    setResumo(null);
    setArquivo(file);
  }
  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    aceitar(e.dataTransfer.files?.[0]);
  }

  // As colunas 13+ do `arquivocarga` são o espaço da prefeitura. O mapa vive na
  // config de campos — é por isso que o editor fica NESTA tela, e não numa aba
  // de configuração distante: quem prepara a carga precisa dos dois juntos.
  const mapeados = campos.filter((c) => typeof c.coluna === "number" && c.coluna >= 13).length;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <header>
        <span
          style={{
            fontSize: 12,
            letterSpacing: "0.1em",
            fontWeight: 700,
            color: "var(--text-dim)",
            textTransform: "uppercase",
          }}
        >
          Averbadora
        </span>
        <h1 style={{ margin: "4px 0 0", fontSize: "1.6rem" }}>Carga inicial</h1>
        <p style={{ color: "var(--text-muted)", margin: "6px 0 0", maxWidth: 760 }}>
          Escolha entre importar o arquivo de retorno existente ou fazer o tombamento completo com servidores,
          margens, rubricas e contratos legados em um único pacote.
        </p>
      </header>

      <Card>
        <div
          style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}
        >
          <SelectField
            label="Prefeitura"
            value={prefId}
            onChange={(e) => {
              setPrefId(e.target.value);
              setResumo(null);
              setRascunho(null);
            }}
            options={[
              { value: "", label: "Selecione…" },
              ...(prefeituras.data?.prefeituras ?? []).map((p) => ({
                value: String(p.id),
                label: rotuloUnidade(p),
              })),
            ]}
          />
          <SelectField
            label="Tipo de carga"
            value={formato}
            onChange={(e) => {
              setFormato(e.target.value as "retorno" | "carga_inicial");
              setResumo(null);
              setArquivo(null);
            }}
            options={[
              { value: "retorno", label: "Arquivo de retorno" },
              { value: "carga_inicial", label: "Carga inicial completa" },
            ]}
          />
          <label style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 13 }}>
            <span style={{ fontWeight: 600 }}>Competência da base</span>
            <input
              value={competencia}
              onChange={(e) => setCompetencia(e.target.value)}
              placeholder="202608 (vazio = mês atual)"
              style={{
                padding: "8px 10px",
                borderRadius: 8,
                border: "1px solid var(--border)",
                background: "transparent",
                color: "inherit",
              }}
            />
            <span style={{ color: "var(--text-dim)", fontSize: 11 }}>
              É o mês de referência da base, não de uma folha aberta.
            </span>
          </label>
        </div>
      </Card>

      {prefId ? (
        <>
          {formato === "retorno" ? (
            <Card>
              <h2 style={{ margin: "0 0 4px", fontSize: "1.05rem" }}>Campos personalizados</h2>
              <p style={{ color: "var(--text-muted)", margin: "0 0 12px", fontSize: 13 }}>
                O layout tem 12 colunas fixas. Da 13ª em diante é espaço desta prefeitura: informe a coluna em
                cada campo para que a célula seja lida na carga. Coluna sem mapeamento é ignorada — e aparece
                no resumo do envio, que é como se descobre um mapa desalinhado.
                {mapeados > 0 ? ` Hoje: ${mapeados} campo(s) mapeado(s).` : ""}
              </p>
              <CamposEditor
                campos={campos}
                onChange={setRascunho}
                onSave={() => saveConfig.mutate(campos)}
                onSaveWith={(lista) => saveConfig.mutate(lista)}
                onRestoreDefault={() => setRascunho(DEFAULT_CAMPOS_FALLBACK)}
                saving={saveConfig.isPending}
                dirty={dirty}
              />
            </Card>
          ) : null}

          <Card>
            <h2 style={{ margin: "0 0 4px", fontSize: "1.05rem" }}>Enviar o zip</h2>
            <p style={{ color: "var(--text-muted)", margin: "0 0 12px", fontSize: 13 }}>
              {formato === "carga_inicial" ? (
                <>
                  Envie <code>ALECE-SERVIDOR-AAMM.CSV</code>, <code>ALECE-FOLHA-AAMM.CSV</code> e{" "}
                  <code>ALECE-CONTRATOS-AAMM.xlsx</code> no mesmo ZIP. A planilha cria os contratos; o arquivo
                  FOLHA apenas confere quantidades e valores. Os <code>.TXT</code> de mesmo nome são do
                  retorno mensal e não entram aqui.
                </>
              ) : (
                <>
                  Reenviar é seguro: a identidade de cada dívida é determinística, então nada duplica e
                  parcela já paga não volta.{" "}
                  <a href={atlas.prefeitura.retornoLayoutTecnicoUrl()} target="_blank" rel="noreferrer">
                    Layout técnico do arquivo
                  </a>
                  {" — é o mesmo documento que a prefeitura usa para o retorno."}
                </>
              )}
            </p>
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              onClick={() => inputRef.current?.click()}
              style={{
                border: `1px dashed ${dragOver ? "var(--gold-500)" : "var(--border)"}`,
                borderRadius: 10,
                padding: 24,
                textAlign: "center",
                cursor: "pointer",
                background: dragOver ? "color-mix(in srgb, var(--gold-500) 8%, transparent)" : "transparent",
              }}
            >
              <input
                ref={inputRef}
                type="file"
                accept=".zip,application/zip"
                style={{ display: "none" }}
                onChange={(e) => aceitar(e.target.files?.[0])}
              />
              {arquivo ? (
                <span>
                  <strong>{arquivo.name}</strong> · {(arquivo.size / 1024).toFixed(0)} KB
                </span>
              ) : (
                <span style={{ color: "var(--text-muted)" }}>
                  Arraste o zip aqui, ou clique para escolher
                </span>
              )}
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
              <Button
                disabled={!arquivo || enviar.isPending}
                onClick={() => arquivo && enviar.mutate(arquivo)}
              >
                {enviar.isPending ? "Enviando…" : "Aplicar carga"}
              </Button>
              {arquivo ? (
                <Button
                  variant="ghost"
                  onClick={() => {
                    setArquivo(null);
                    setResumo(null);
                  }}
                >
                  Limpar
                </Button>
              ) : null}
            </div>
            <AndamentoEnvio
              ativo={enviar.isPending}
              consequencia="Não feche esta página: a carga entra inteira ou não entra, e o resumo aparece aqui no fim."
            />
            {enviar.isError ? (
              <p style={{ color: "var(--danger-500)", marginTop: 10, fontSize: 13 }}>
                {(enviar.error as Error).message}
              </p>
            ) : null}
          </Card>
        </>
      ) : null}

      {resumo ? <ResumoCarga r={resumo} /> : null}
    </div>
  );
}

/** Conta ocorrencias iguais, da mais frequente para a menos. */
function agruparMotivos(motivos: string[]): [string, number][] {
  const m = new Map<string, number>();
  for (const x of motivos) m.set(x, (m.get(x) ?? 0) + 1);
  return [...m.entries()].sort((a, b) => b[1] - a[1]);
}

function ResumoCarga({ r }: { r: AdminCargaInicialResumo }) {
  if (r.formato === "carga_inicial" && r.lote) {
    const l = r.lote;
    return (
      <Card>
        <h2 style={{ margin: "0 0 12px", fontSize: "1.05rem" }}>
          Carga ALECE {l.status === "aplicado" ? "aplicada" : "aplicada com pendências"} · {l.competencia}
        </h2>
        {r.idempotente ? (
          <p style={{ fontSize: 13, color: "var(--text-muted)" }}>
            Este conteúdo já havia sido aplicado. Nenhum registro foi duplicado.
          </p>
        ) : null}
        {r.arquivos_reconhecidos ? (
          <p style={{ fontSize: 12, color: "var(--text-muted)" }}>
            Reconhecidos: {Object.values(r.arquivos_reconhecidos).join(" · ")}
          </p>
        ) : null}
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap", fontSize: 14 }}>
          <span>
            <strong>{l.servidores}</strong> servidores
          </span>
          <span>
            <strong>{l.margens}</strong> margens
          </span>
          <span>
            <strong>{l.rubricas}</strong> rubricas
          </span>
          <span>
            <strong>{l.contratosCriados}</strong> contratos criados
          </span>
          <span>
            <strong>{l.contratosAtualizados}</strong> atualizados
          </span>
          <span>
            <strong>{l.pendencias}</strong> pendências
          </span>
          <span>
            <strong>{l.divergencias}</strong> divergências
          </span>
        </div>
        {r.divergencias?.length ? (
          <ul style={{ fontSize: 12, marginTop: 14, paddingLeft: 18 }}>
            {r.divergencias.slice(0, 30).map((d, i) => (
              <li key={`${d.tipo}-${d.matricula}-${d.rubrica}-${i}`}>
                <code>
                  {d.matricula} / {d.rubrica}
                </code>{" "}
                · {d.mensagem}
              </li>
            ))}
          </ul>
        ) : null}
        {r.arquivo_id ? (
          <p style={{ marginTop: 14, fontSize: 12 }}>
            <a
              href="#"
              onClick={async (e) => {
                e.preventDefault();
                const blob = await atlas.admin.trocaArquivoTombamento(r.arquivo_id!);
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `tombamento-alece-${l.competencia}.json`;
                a.click();
                URL.revokeObjectURL(url);
              }}
            >
              Baixar relatório de pendências e tombamento
            </a>
          </p>
        ) : null}
      </Card>
    );
  }
  if (r.rejeitado) {
    return (
      <Card style={{ borderColor: "color-mix(in srgb, var(--danger-500) 55%, transparent)" }}>
        <h2 style={{ margin: "0 0 6px", fontSize: "1.05rem" }}>Carga recusada</h2>
        <p style={{ margin: 0, color: "var(--text-muted)", fontSize: 13 }}>{r.motivo_rejeicao}</p>
      </Card>
    );
  }
  return (
    <Card>
      <h2 style={{ margin: "0 0 12px", fontSize: "1.05rem" }}>Carga aplicada · {r.competencia}</h2>
      {r.carteiraLegada ? <p>Carteira legada: <strong>{r.carteiraLegada.contratosVivos}</strong> contratos em aberto ({r.carteiraLegada.ativos} ativos e {r.carteiraLegada.suspensos} com desconto suspenso), além de {r.carteiraLegada.baixasExternas} baixas externas. {r.carteiraLegada.cadastrosPendentes} contratos aguardam cadastro da folha.</p> : null}
      {/* O que precisa ser lido ANTES de dar a carga por boa. Hoje é um aviso
          só — o arquivo declara outra competência — e ele vem primeiro porque
          é a diferença entre um mês certo e um mês errado em cada ADF. */}
      {r.avisos?.length ? (
        <div
          role="alert"
          style={{
            margin: "0 0 14px",
            padding: "10px 12px",
            borderRadius: 8,
            border: "1px solid color-mix(in srgb, var(--gold-500) 55%, transparent)",
            background: "color-mix(in srgb, var(--gold-500) 10%, transparent)",
            fontSize: 13,
          }}
        >
          <strong>Confira antes de seguir</strong>
          <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
            {r.avisos.map((a) => (
              <li key={a}>{a}</li>
            ))}
          </ul>
        </div>
      ) : null}
      <div style={{ display: "flex", gap: 24, flexWrap: "wrap", fontSize: 14 }}>
        <span>
          <strong>{r.servidores_novos}</strong> servidor(es) novo(s)
        </span>
        <span>
          <strong>{r.dividas_herdadas_novas}</strong> dívida(s) herdada(s)
        </span>
        {r.dados_bancarios_atualizados ? (
          <span>
            <strong>{r.dados_bancarios_atualizados}</strong> conta(s) atualizada(s)
          </span>
        ) : null}
        {r.cronogramas_alinhados ? (
          <span title="Dívidas legadas que já existiam e estavam com a parcela diferente da que o arquivo informa. A folha manda no cronograma do legado.">
            <strong>{r.cronogramas_alinhados}</strong> cronograma(s) alinhado(s) ao arquivo
            {r.quitados_pela_folha ? (
              <>
                {" "}
                · <strong>{r.quitados_pela_folha}</strong> quitada(s) na última parcela
              </>
            ) : null}
          </span>
        ) : null}
      </div>

      {r.linhas_indistinguiveis?.length ? (
        <div style={{ marginTop: 16 }}>
          <strong style={{ fontSize: 13 }}>
            {r.linhas_indistinguiveis.length} linha(s) indistinguível(is)
          </strong>
          <p style={{ color: "var(--text-muted)", fontSize: 12, margin: "4px 0 8px", maxWidth: 720 }}>
            Sem número de contrato, duas linhas iguais em verba, descrição e valor não têm como ser separadas.
            A segunda <strong>não</strong> virou dívida nova (duplicaria margem) e <strong>não</strong> foi
            somada à primeira (esconderia uma dívida real). Confira na origem e reenvie com o número de
            contrato preenchido.
          </p>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, fontFamily: "var(--font-mono)" }}>
            {/* `adf` vem `null` quando a colisão foi com outra linha do mesmo
                pacote que ficou em QUARENTENA: ali ainda não há contrato, e
                inventar um rótulo esconderia que o desconto está esperando
                convênio, não conferência de duplicata. */}
            {r.linhas_indistinguiveis
              .slice(0, 20)
              .map((l: { linha: number; matricula: string; adf: string | null }) => (
                <li key={`${l.linha}-${l.adf ?? "quarentena"}`}>
                  linha {l.linha} · matrícula {l.matricula} · {l.adf ?? "em quarentena, sem ADF"}
                </li>
              ))}
          </ul>
        </div>
      ) : null}

      {r.colunas_sem_mapeamento?.length ? (
        <p style={{ marginTop: 14, fontSize: 13, color: "var(--text-muted)" }}>
          Coluna(s) <strong>{r.colunas_sem_mapeamento.join(", ")}</strong> vieram preenchidas e nenhum campo
          as reivindica. Não é erro — mas se você esperava lê-las, o mapa acima aponta para a coluna errada.
        </p>
      ) : null}

      {r.nao_tombados?.length ? (
        <div style={{ marginTop: 16 }}>
          <strong style={{ fontSize: 13 }}>{r.nao_tombados.length} desconto(s) não tombado(s)</strong>
          <p style={{ color: "var(--text-muted)", fontSize: 12, margin: "4px 0 8px", maxWidth: 720 }}>
            Linhas de consignação cujo servidor está sem salário, desligado ou fora da base. Não é erro: sem
            salário não há margem a calcular. O primeiro retorno em que o salário vier tomba a linha sozinho.
          </p>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, fontFamily: "var(--font-mono)" }}>
            {agruparMotivos(r.nao_tombados.map((n) => n.motivo)).map(([motivo, n]) => (
              <li key={motivo}>
                {n} × {motivo}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {r.reativados?.length ? (
        <p style={{ marginTop: 14, fontSize: 13, color: "var(--text-muted)" }}>
          <strong>{r.reativados.length}</strong> dívida(s) que estavam quitadas ou encerradas voltaram a
          descontar na folha e foram reativadas, com a mesma ADF.
        </p>
      ) : null}

      {r.linhas_rejeitadas?.length ? (
        <div style={{ marginTop: 14 }}>
          <p style={{ margin: 0, fontSize: 13, color: "var(--danger-500)" }}>
            {r.linhas_rejeitadas.length} linha(s) recusada(s). As demais entraram — a carga não é
            tudo-ou-nada.
          </p>
          {/* O MOTIVO, agrupado. Ate 09/09/2026 a tela mostrava so a contagem, e o
              operador nao tinha como saber que 1.187 recusas eram uma unica causa
              (percentual na coluna 9) que o ERP resolve numa linha. */}
          <ul style={{ margin: "6px 0 0", paddingLeft: 18, fontSize: 12, fontFamily: "var(--font-mono)" }}>
            {agruparMotivos(
              (
                r.linhas_rejeitadas as {
                  arquivo?: string;
                  erros?: { campo: string; coluna: number; mensagem: string }[];
                }[]
              ).flatMap((l) =>
                (l.erros ?? []).map(
                  (e) =>
                    `${l.arquivo ?? "?"} · coluna ${e.coluna} (${e.campo}) · ${e.mensagem.replace(/"[^"]*"/, "…")}`,
                ),
              ),
            )
              .slice(0, 12)
              .map(([motivo, n]) => (
                <li key={motivo}>
                  {n} × {motivo}
                </li>
              ))}
          </ul>
        </div>
      ) : null}

      {r.arquivo_id ? (
        <p style={{ marginTop: 14, fontSize: 12 }}>
          <a
            href="#"
            onClick={async (e) => {
              e.preventDefault();
              const blob = await atlas.admin.trocaArquivoTombamento(r.arquivo_id!);
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = `tombamento-${r.competencia}.json`;
              a.click();
              URL.revokeObjectURL(url);
            }}
          >
            Baixar o relatório de tombamento
          </a>
          {" — contratos criados, não tombados, quarentena e recusas. Fica guardado com o pacote."}
        </p>
      ) : null}

      {/* VERBAS EM QUARENTENA.
          A carga deixou de ser recusada por verba desconhecida, então esta lista
          é o que substitui a mensagem de erro que antes dizia o que faltava.
          Sem ela, a importação concluiria e o operador não teria como saber que
          parte do contracheque ficou sem credor. Ordenada pelo valor, que é o
          que decide por onde começar. */}
      {r.verbas_pendentes?.length ? (
        <div style={{ marginTop: 18, paddingTop: 14, borderTop: "1px solid var(--border)" }}>
          <h3 style={{ margin: "0 0 4px", fontSize: 14 }}>
            {r.verbas_pendentes.length} verba(s) sem convênio
          </h3>
          <p style={{ margin: "0 0 10px", fontSize: 12, color: "var(--text-muted)", lineHeight: 1.5 }}>
            O desconto delas está medido, mas não tem credor nem produto — não vira dívida e não abate balde
            de margem. Cadastrar um convênio que declare cada uma resolve, sem reimportar.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {r.verbas_pendentes.map((v) => (
              <div
                key={v.codigo_verba}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 12,
                  fontSize: 13,
                  padding: "6px 10px",
                  borderRadius: 6,
                  background: "color-mix(in srgb, var(--gold-500) 8%, transparent)",
                }}
              >
                <span>
                  <code>{v.codigo_verba}</code>{" "}
                  <span style={{ color: "var(--text-muted)" }}>{v.descricao}</span>
                  {v.no_catalogo ? null : (
                    <span style={{ color: "var(--text-dim)", fontSize: 12 }}> · nunca vista antes</span>
                  )}
                </span>
                <span style={{ whiteSpace: "nowrap", color: "var(--text-muted)" }}>
                  {v.servidores} servidor(es) ·{" "}
                  {v.valor.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                </span>
              </div>
            ))}
          </div>
          <p style={{ margin: "10px 0 0", fontSize: 12 }}>
            <a href="/averbadora/convenios">Cadastrar convênio →</a>
          </p>
        </div>
      ) : null}

      {r.ignoradas_por_catalogo ? (
        <p style={{ marginTop: 14, fontSize: 13, color: "var(--text-muted)" }}>
          {r.ignoradas_por_catalogo} linha(s) de desconto ignorada(s) por não serem consignação — previdência,
          imposto, sindicato. Está certo: elas nunca deveriam virar dívida.
        </p>
      ) : null}

      {r.catalogo ? (
        <p style={{ marginTop: 8, fontSize: 12, color: "var(--text-dim)" }}>
          Holerite do pacote: {r.catalogo.descobertas} verba(s) vista(s), {r.catalogo.novas} nova(s) no
          catálogo desta folha.
        </p>
      ) : null}
    </Card>
  );
}
